import { Autocomplete, Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getActiveTicket } from 'store/ticket-service/selector';
import {
	createTicketShare,
	getTicketShares,
	revokeTicketSharePermission,
	updateTicketSharePermission,
} from 'store/ticket-share-service/asyncActions';
import {
	getCurrentTicketShares,
	getIsCurrentTicketSharesLoading,
} from 'store/ticket-share-service/selector';
import { setIsCreateTicketShareOpen } from 'store/ticket-share-service/ticketShareSlice';
import { findUserByEmail } from 'store/user-service/asyncActions';

import { NamespacesList, SharePermissions, TicketStages } from 'core/enums/common';
import useDebounce from 'core/hooks/useDebounce';
import { useGetUser } from 'core/hooks/useGetUser';
import { FoundUser } from 'core/types/base';

import { ReactComponent as ArrowLeft } from 'assets/icons/arrow-left.svg';
import { regularTextColor } from 'assets/styles/_palette';

import { UserCard } from '../UserCard';

import {
	allStagesEnum,
	allSubStagesEnum,
	getStagesInfo,
	permissionsInfo,
	subStagesInfo,
} from './data';
import * as Styled from './styled';
import {
	IShareOptions,
	TicketStageOptions,
	TicketStageOptionsValues,
	TicketSubStageOptions,
} from './types';

export interface ISharePermissionEdit {
	permission: SharePermissions;
	shareId: string;
}

const selectInputStyles: Record<string, string> = {
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
};

export const CreateTicketShareModal: FC = () => {
	const { t: modalTranslate, i18n } = useTranslation(NamespacesList['modals']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const { user } = useGetUser();

	const activeTicket = useSelector(getActiveTicket);
	const currentTicketShares = useSelector(getCurrentTicketShares);
	const isCurrentTicketSharesLoading = useSelector(getIsCurrentTicketSharesLoading);

	const [isPermissionRequestLoading, setIsPermissionRequestLoading] = useState(false);
	const [userSearchQuery, setUserSearchQuery] = useState('');
	const [userSearchResults, setUserSearchResults] = useState<Array<FoundUser>>([]);
	const [selectedUser, setSelectedUser] = useState<FoundUser | null>(null);
	const [selectedSharesIds, setSelectedSharesIds] = useState<Array<string>>([]);
	const [updatedSharesPermissions, setUpdatedSharesPermissions] = useState<
		Array<ISharePermissionEdit>
	>([]);
	const [shareOptions, setShareOptions] = useState<IShareOptions>({
		permission: SharePermissions.Read,
		stage: [TicketStageOptions.general],
		subStage: [TicketSubStageOptions['Functional requirements']],
	});

	const debouncedUserSearchQuery = useDebounce(userSearchQuery, 400);

	const handleUserSearch = async (searchValue: string) => {
		const foundUser = await dispatch(findUserByEmail({ emailPrefix: searchValue })).unwrap();

		setUserSearchResults(foundUser);
	};

	useEffect(() => {
		dispatch(getTicketShares({ ticketId: activeTicket?.id || '' }));
	}, []);

	useEffect(() => {
		if (debouncedUserSearchQuery.length >= 1) handleUserSearch(debouncedUserSearchQuery);
		else setUserSearchResults([]);
	}, [debouncedUserSearchQuery]);

	useEffect(() => {
		const currentUserPermissions = currentTicketShares.filter(
			(share) => share.userDto.id === selectedUser?.id
		);

		const selectedUserTicketStagesPermissions = [
			...new Set(
				currentUserPermissions.map((permission) =>
					permission.ticketCurrentStep === null ? allStagesEnum : permission.ticketCurrentStep
				)
			),
		].sort((a, b) => a - b);

		const selectedUserTicketSubStagesPermissions = [
			...new Set(
				currentUserPermissions
					.filter((share) => share.ticketCurrentStep === TicketStages.general)
					.map((permission) =>
						permission.subStageEnum === null ? allSubStagesEnum : permission.subStageEnum
					)
			),
		].sort((a, b) => a - b);

		setShareOptions({
			permission:
				currentUserPermissions?.[0]?.permission !== undefined
					? currentUserPermissions[0].permission
					: shareOptions.permission,
			stage: selectedUserTicketStagesPermissions,
			subStage: selectedUserTicketSubStagesPermissions,
		});
	}, [selectedUser, currentTicketShares]);

	const handleAutocompleteClose = () => {
		if (!selectedUser) setUserSearchResults([]);
	};

	const autoCompleteOptions = useMemo(
		() => userSearchResults.map((user: any) => user.email).filter((email) => email !== user.email),
		[userSearchResults]
	);

	const areUserPermissionsChanged = useMemo(() => {
		const currentUserPermissions = currentTicketShares.filter(
			(share) => share.userDto.id === selectedUser?.id
		);

		const sharedWithUserStages = [
			...new Set(
				currentUserPermissions.map((share) =>
					share.ticketCurrentStep === null ? allStagesEnum : share.ticketCurrentStep
				)
			),
		];

		const sharedWithUserSubStages = [
			...new Set(
				currentUserPermissions
					.filter((permission) => permission.ticketCurrentStep === TicketStages.general)
					.map((share) => (share.subStageEnum === null ? allSubStagesEnum : share.subStageEnum))
			),
		];

		return (
			shareOptions.stage.some((permission) => !sharedWithUserStages.includes(permission)) ||
			shareOptions.subStage.some((permission) => !sharedWithUserSubStages.includes(permission))
		);
	}, [currentTicketShares, shareOptions]);

	const isSubStageSelected = useMemo(() => {
		if (shareOptions.stage.includes(TicketStages.general)) return shareOptions.subStage.length;

		return true;
	}, [shareOptions]);

	const getSelectRenderValue = (value: Array<number>, isStage: boolean) => {
		const stagesInfo = getStagesInfo({
			isUseCasesReady: true,
			isTestCasesReady: true,
			isNotificationsReady: true,
			isReportReady: true,
		});

		const parsedValue = value.map((item) =>
			isStage
				? item === allStagesEnum
					? commonTranslate('text.all')
					: commonTranslate(`stepper.${stagesInfo[item]?.label}`) || ''
				: item === allSubStagesEnum
				? commonTranslate('text.all')
				: commonTranslate(`modelsInput.${subStagesInfo[item]?.label}`) || ''
		);

		if (parsedValue.length === 0)
			return <Typography sx={{ ...selectInputStyles, color: regularTextColor }}></Typography>;

		return (
			<Typography sx={{ ...selectInputStyles, color: regularTextColor }}>
				{parsedValue.join(', ')}
			</Typography>
		);
	};

	const handleSetSelectedUser = (value: string) => {
		setSelectedUser(userSearchResults?.find((user) => user.email === value) || null);
	};

	const handlePermissionChange = (value: string | SharePermissions) => {
		setShareOptions({ ...shareOptions, permission: Number(value) });
	};

	const handleStageChange = (value: string | Array<TicketStageOptionsValues>) => {
		const parsedValue = typeof value === 'string' ? [Number(value)] : value;

		const updatedValue = parsedValue.includes(allStagesEnum) ? [allStagesEnum] : parsedValue;

		setShareOptions({
			...shareOptions,
			stage: updatedValue,
		});
	};

	const handleSubStageChange = (value: string | Array<TicketStageOptionsValues>) => {
		const parsedValue = typeof value === 'string' ? [Number(value)] : value;

		const updatedValue = parsedValue.includes(allSubStagesEnum) ? [allSubStagesEnum] : parsedValue;

		setShareOptions({
			...shareOptions,
			subStage: updatedValue,
		});
	};

	const handleShareRequestSend = async () => {
		if (selectedUser && activeTicket) {
			setIsPermissionRequestLoading(true);

			const parsedStagePermission = shareOptions.stage.includes(allStagesEnum)
				? null
				: [...new Set(shareOptions.stage)];

			const parsedSubStagePermission = shareOptions.subStage.includes(allSubStagesEnum)
				? null
				: [...new Set(shareOptions.subStage)];

			const currentUserPermissions = currentTicketShares.filter(
				(share) => share.userDto.id === selectedUser.id
			);

			const sharedWithUserStages = [
				...new Set(
					currentUserPermissions.map((share) =>
						share.ticketCurrentStep === null ? allStagesEnum : share.ticketCurrentStep
					)
				),
			];

			const sharedWithUserSubStages = [
				...new Set(
					currentUserPermissions.map((share) =>
						share.subStageEnum === null ? allSubStagesEnum : share.subStageEnum
					)
				),
			];

			const defaultBody = {
				shareUserId: selectedUser.id,
				ticketId: activeTicket.id,
				permission: shareOptions.permission,
			};

			if (currentUserPermissions.length) {
				const newStagePermissions =
					parsedStagePermission === null
						? null
						: parsedStagePermission.filter(
								(permission) => !sharedWithUserStages.includes(permission)
						  );

				const newSubStagePermissions =
					parsedSubStagePermission === null
						? null
						: parsedSubStagePermission.filter(
								(permission) => !sharedWithUserSubStages.includes(permission)
						  );

				const deletedStagePermissions =
					parsedStagePermission === null
						? currentUserPermissions
						: currentUserPermissions.filter(
								(permission) =>
									!parsedStagePermission.includes(
										permission.ticketCurrentStep === null
											? allStagesEnum
											: permission.ticketCurrentStep
									)
						  );

				const deletedSubStagePermissions =
					parsedSubStagePermission === null
						? currentUserPermissions.filter(
								(permission) => permission.ticketCurrentStep === TicketStages.general
						  )
						: currentUserPermissions.filter(
								(permission) =>
									!parsedSubStagePermission.includes(
										permission.subStageEnum === null ? allSubStagesEnum : permission.subStageEnum
									) && permission.ticketCurrentStep === TicketStages.general
						  );

				if (currentUserPermissions) {
					await dispatch(
						createTicketShare({
							...defaultBody,
							ticketCurrentStep: newStagePermissions,
							subStageEnum: newSubStagePermissions,
						})
					);

					const deletedIds = [
						...new Set(
							[...deletedStagePermissions, ...deletedSubStagePermissions].map(
								(permission) => permission.id
							)
						),
					];

					await dispatch(revokeTicketSharePermission({ shareIds: deletedIds }));
				}
			} else {
				await dispatch(
					createTicketShare({
						...defaultBody,
						ticketCurrentStep: parsedStagePermission,
						subStageEnum: parsedSubStagePermission,
					})
				);
			}

			await dispatch(getTicketShares({ ticketId: activeTicket?.id || '' }));

			setIsPermissionRequestLoading(false);
		}
	};

	const handlePermissionRevoke = async () => {
		await dispatch(revokeTicketSharePermission({ shareIds: selectedSharesIds }));

		setSelectedSharesIds([]);
	};

	const handlePermissionEdit = (permission: ISharePermissionEdit) => {
		setUpdatedSharesPermissions((prevState) => {
			const isPermissionEdited = prevState.filter(
				(permissionItem) => permissionItem.shareId === permission.shareId
			).length;

			if (isPermissionEdited)
				return prevState.map((permissionItem) =>
					permissionItem.shareId === permission.shareId ? permission : permissionItem
				);
			else return [...prevState, permission];
		});
	};

	const savePermissionsEdit = async () => {
		for await (const editedShare of updatedSharesPermissions) {
			dispatch(updateTicketSharePermission({ editedPermission: editedShare }));
		}

		setUpdatedSharesPermissions([]);
	};

	const handleShareSelect = (shareId: string) => {
		const isExistingItem = selectedSharesIds.includes(shareId);

		if (!isExistingItem) setSelectedSharesIds((prevState) => [...prevState, shareId]);
		else setSelectedSharesIds((prevState) => prevState.filter((id) => id !== shareId));
	};

	const handleLeaveShare = () => {
		dispatch(setIsCreateTicketShareOpen(false));
	};

	const isUseCasesReady = useMemo(() => {
		return Boolean(
			activeTicket?.ticketSummaries?.length &&
				activeTicket?.ticketSummaries?.every(
					(summary) => summary.summaryAcceptanceCriteria?.length && summary.summaryScenarios?.length
				)
		);
	}, [activeTicket?.id, activeTicket?.ticketSummaries]);

	const isTestCasesReady = useMemo(() => {
		return Boolean(isUseCasesReady && activeTicket?.useCases?.length);
	}, [activeTicket?.id, activeTicket?.useCases, isUseCasesReady]);

	const isNotificationsReady = useMemo(() => {
		return Boolean(isTestCasesReady && activeTicket?.userStoryTests?.length);
	}, [activeTicket?.id, activeTicket?.notifications, isTestCasesReady]);

	const isReportReady = useMemo(() => {
		return Boolean(isNotificationsReady && activeTicket?.notifications?.length);
	}, [activeTicket?.id, activeTicket?.notifications]);

	const stagesInfo = useMemo(
		() => getStagesInfo({ isUseCasesReady, isTestCasesReady, isNotificationsReady, isReportReady }),
		[isUseCasesReady, isTestCasesReady, isNotificationsReady, isReportReady]
	);

	return (
		<Styled.ModalContent className="not-rtl-able">
			<Styled.ModalTitle>{modalTranslate('allowAccessModal.title')}</Styled.ModalTitle>
			<Styled.ModalMainContent className="rtl-able">
				<Autocomplete
					disablePortal
					onClose={handleAutocompleteClose}
					onChange={(_, value) => handleSetSelectedUser(value as string)}
					options={autoCompleteOptions}
					noOptionsText={modalTranslate('allowAccessModal.userNotFound')}
					sx={{ width: '100%' }}
					renderInput={(params) => (
						<TextField
							{...params}
							label={modalTranslate('allowAccessModal.addUsers')}
							onChange={(event) => setUserSearchQuery(event.target.value)}
						/>
					)}
					dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
				/>
				<Styled.ModalUsers>
					<Styled.ModalSubtitle>
						{modalTranslate('allowAccessModal.usersWithAccess')}
					</Styled.ModalSubtitle>
				</Styled.ModalUsers>
			</Styled.ModalMainContent>

			<Styled.InputsContainer className="rtl-able">
				<Styled.SingleInputContainer>
					<Styled.SingleInputTitle>
						{modalTranslate('allowAccessModal.permission')}:
					</Styled.SingleInputTitle>
					<Select
						sx={{ width: '100%' }}
						disabled={!selectedUser || !areUserPermissionsChanged}
						value={shareOptions.permission}
						onChange={(event) => handlePermissionChange(event.target.value)}
					>
						{permissionsInfo.map((permission, permissionIndex) => (
							<MenuItem
								key={`${permissionIndex}.${permission.label}`}
								value={permission.permissionEnum}
							>
								{commonTranslate(`accessLevels.${String(permission.label).toLowerCase()}`)}
							</MenuItem>
						))}
					</Select>
				</Styled.SingleInputContainer>

				<Styled.SingleInputContainer>
					<Styled.SingleInputTitle>
						{modalTranslate('allowAccessModal.sharedStage')}:
					</Styled.SingleInputTitle>
					<Select
						sx={{ width: '100%' }}
						multiple
						inputProps={{ style: selectInputStyles }}
						disabled={!selectedUser}
						renderValue={(value) => getSelectRenderValue(value, true)}
						value={shareOptions.stage}
						onChange={(event) => handleStageChange(event.target.value)}
					>
						{stagesInfo.map((stage, stageIndex) => (
							<MenuItem
								key={`${stageIndex}.${stage.label}`}
								value={stage.stageEnum}
								disabled={!stage.isAvailable}
							>
								<Typography>
									{stageIndex === allStagesEnum
										? commonTranslate('text.all')
										: commonTranslate(`stepper.${stage.label}`)}
								</Typography>
							</MenuItem>
						))}
					</Select>
				</Styled.SingleInputContainer>

				<Styled.SingleInputContainer>
					<Styled.SingleInputTitle>
						{modalTranslate('allowAccessModal.sharedSubStage')}:
					</Styled.SingleInputTitle>
					<Select
						sx={{ width: '100%' }}
						multiple
						inputProps={{ style: selectInputStyles }}
						value={shareOptions.subStage}
						renderValue={(value) => getSelectRenderValue(value, false)}
						onChange={(event) => handleSubStageChange(event.target.value)}
						disabled={!selectedUser || !shareOptions.stage.includes(TicketStageOptions.general)}
					>
						{subStagesInfo.map((subStage, subStageIndex) => (
							<MenuItem key={`${subStageIndex}.${subStage.label}`} value={subStage.subStageEnum}>
								{subStageIndex === allSubStagesEnum
									? commonTranslate('text.all')
									: commonTranslate(`modelsInput.${subStage.label}`)}
							</MenuItem>
						))}
					</Select>
				</Styled.SingleInputContainer>
			</Styled.InputsContainer>

			<Styled.ShareButton
				type="submit"
				variant="contained"
				color="primary"
				disabled={
					!selectedUser ||
					isPermissionRequestLoading ||
					!isSubStageSelected ||
					!areUserPermissionsChanged
				}
				onClick={handleShareRequestSend}
			>
				{commonTranslate('buttons.share')}
			</Styled.ShareButton>
			<Styled.BackButton
				sx={{
					left: i18n.language === 'ar' ? 'initial' : '0',
					right: i18n.language === 'ar' ? '0' : 'initial',
				}}
				className="rtl-able"
				onClick={handleLeaveShare}
			>
				<ArrowLeft
					style={{ transform: i18n.language === 'ar' ? `rotate(180deg)` : `rotate(0deg)` }}
				/>
				{commonTranslate('buttons.back')}
			</Styled.BackButton>
			{isCurrentTicketSharesLoading ? (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						width: '100%',
						height: '100%',
					}}
				>
					<Oval
						height={24}
						width={24}
						color="#fff"
						wrapperStyle={{}}
						wrapperClass=""
						visible={true}
						ariaLabel="oval-loading"
						secondaryColor="#91B6FF"
						strokeWidth={5}
						strokeWidthSecondary={5}
					/>
				</Box>
			) : (
				<Styled.SharesContainer className="rtl-able">
					<Styled.SharesTitle>
						{modalTranslate('allowAccessModal.usersWithAccess')}:
					</Styled.SharesTitle>
					<Styled.SharesItemsContainer
						sx={{ display: 'flex', gap: '16px', flexDirection: 'column' }}
					>
						{[...currentTicketShares]
							.sort((a, b) => (a.userDto.id > b.userDto.id ? 1 : -1))
							.map((share) => (
								<UserCard
									key={share.id}
									ticketShareData={share}
									isSelected={selectedSharesIds.includes(share.id)}
									handleShareSelect={handleShareSelect}
									handlePermissionUpdate={handlePermissionEdit}
								/>
							))}
					</Styled.SharesItemsContainer>

					<Styled.SharesButtonsContainer>
						<Styled.ShareActionButton
							variant="contained"
							color="primary"
							disabled={!selectedSharesIds.length}
							onClick={handlePermissionRevoke}
						>
							<Typography>{commonTranslate('menu.delete')}</Typography>
						</Styled.ShareActionButton>
						<Styled.ShareActionButton
							variant="contained"
							color="primary"
							disabled={!updatedSharesPermissions.length}
							onClick={savePermissionsEdit}
						>
							<Typography>{commonTranslate('buttons.save')}</Typography>
						</Styled.ShareActionButton>
					</Styled.SharesButtonsContainer>
				</Styled.SharesContainer>
			)}
		</Styled.ModalContent>
	);
};
